import React, { useEffect, useRef, useState } from 'react';
import { API, isAdmin } from '../../helpers';
import LogsTable from '../../components/LogsTable';
import {
  label,
  actions,
  parseJSON,
  pretty,
  projectEvents,
  changes,
  fieldNames,
} from './activity';
import './activity.css';
import ListDivider from './ListDivider';
import PromptContent from './PromptContent';

const initialFilters = {
  q: new URLSearchParams(window.location.search).get('request') || '',
  kind: 'all',
  outcome: '',
  model: '',
  channel_id: '',
  user_id: '',
  from: '',
  to: '',
};
const time = (value) => new Date(value * 1000).toLocaleString();
const display = (value) =>
  value === null || value === undefined || value === ''
    ? '—'
    : typeof value === 'boolean'
    ? value
      ? '是'
      : '否'
    : typeof value === 'object'
    ? pretty(value)
    : String(value);

function Fields({ value }) {
  if (!value || typeof value !== 'object')
    return <p className='trace-muted'>{display(value)}</p>;
  return (
    <dl className='trace-fields'>
      {Object.entries(value).map(([key, v]) => (
        <React.Fragment key={key}>
          <dt>{fieldNames[key] || key}</dt>
          <dd>{display(v)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
function Timeline({ events, total, selected, onSelect }) {
  const duration = Math.max(total || 0, 1);
  return (
    <div className='trace-timeline'>
      <div className='trace-axis'>
        <span>0 ms</span>
        <span>{total} ms</span>
      </div>
      {events
        .filter((e) => e.at !== null)
        .map((event) => {
          const start =
            event.start ??
            (event.duration
              ? Math.max(0, event.at - event.duration)
              : event.at);
          const left = Math.min(100, Math.max(0, (start / duration) * 100));
          const width = Math.min(
            100 - left,
            Math.max(event.duration ? (event.duration / duration) * 100 : 0, 0)
          );
          return (
            <button
              type='button'
              key={event.id}
              className={`trace-span ${event.failed ? 'failed' : ''} ${
                selected === event.id ? 'selected' : ''
              }`}
              onClick={() => onSelect(event.id)}
            >
              <span>{event.title}</span>
              <div className='trace-track'>
                <i
                  style={{
                    left: width
                      ? `${left}%`
                      : `calc(${left}% - ${left === 100 ? 3 : 0}px)`,
                    width: width ? `${width}%` : '3px',
                  }}
                />
              </div>
              <small>
                {event.duration !== undefined
                  ? `${event.duration} ms`
                  : `${event.at} ms`}
              </small>
            </button>
          );
        })}
      <p className='trace-muted'>
        横轴为请求开始后的累计时间。钱包和对象快照没有独立时间戳，请在记录视图查看。
      </p>
    </div>
  );
}

export default function ActivityWorkspace() {
  const [mobilePane, setMobilePane] = useState(() => new URLSearchParams(window.location.search).has('request') ? 'detail' : 'list');
  const [section, setSection] = useState('activity');
  const [draft, setDraft] = useState(initialFilters),
    [filters, setFilters] = useState(initialFilters),
    [page, setPage] = useState(0);
  const [rows, setRows] = useState([]),
    [total, setTotal] = useState(0),
    [loading, setLoading] = useState(false),
    [error, setError] = useState('');
  const [selection, setSelection] = useState(null),
    [storedDetail, setStoredDetail] = useState(null),
    [detailError, setDetailError] = useState(''),
    [detailLoading, setDetailLoading] = useState(false);
  const [view, setView] = useState('records'),
    [eventId, setEventId] = useState(''),
    [refresh, setRefresh] = useState(0);
  const workspaceRef = useRef(null);
  const [listWidth, setListWidth] = useState(() =>
    Math.max(
      200,
      Math.min(
        520,
        Number(localStorage.getItem('atc_activity_list_width')) || 260
      )
    )
  );
  const [maximumWidth, setMaximumWidth] = useState(520);
  const persistWidth = (width) => {
    try {
      localStorage.setItem('atc_activity_list_width', String(width));
    } catch {}
  };
  const resizeList = (width, persist = false) => {
    const next = Math.max(200, Math.min(maximumWidth, width));
    setListWidth(next);
    if (persist) persistWidth(next);
  };
  useEffect(() => {
    if (section !== 'activity') return;
    const update = () => {
      const width =
        workspaceRef.current?.getBoundingClientRect().width ||
        window.innerWidth - 64;
      const maximum = Math.max(
        200,
        Math.min(520, width - (window.innerWidth > 1200 ? 626 : 306))
      );
      setMaximumWidth(maximum);
      setListWidth((current) => Math.max(200, Math.min(maximum, current)));
    };
    update();
    window.addEventListener('resize', update);
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer && workspaceRef.current)
      observer.observe(workspaceRef.current);
    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [section]);
  const listVersion = useRef(0),
    detailVersion = useRef(0);
  useEffect(() => {
    if (section !== 'activity') return;
    const version = ++listVersion.current;
    setLoading(true);
    setError('');
    const query = new URLSearchParams({
      page: String(page),
      kind: filters.kind,
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'kind') {
        query.set(
          key,
          key === 'from' || key === 'to'
            ? String(Math.floor(new Date(value).getTime() / 1000))
            : value
        );
      }
    });
    API.get(`/api/platform/activity?${query}`)
      .then((response) => {
        if (version !== listVersion.current) return;
        if (!response.data.success) throw new Error(response.data.message);
        const value = response.data.data;
        setRows(value.items);
        setTotal(value.total);
        setSelection(
          (current) =>
            value.items.find(
              (r) =>
                r.kind === current?.kind && r.request_id === current?.request_id
            ) ||
            value.items[0] ||
            null
        );
      })
      .catch((e) => {
        if (version === listVersion.current) {
          setError(e.response?.data?.message || e.message);
          setRows([]);
          setTotal(0);
          setSelection(null);
        }
      })
      .finally(() => {
        if (version === listVersion.current) setLoading(false);
      });
    return () => {
      listVersion.current++;
    };
  }, [filters, page, refresh, section]);
  useEffect(() => {
    const version = ++detailVersion.current;
    setStoredDetail(null);
    setDetailError('');
    setEventId('');
    if (!selection) {
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    const path = selection.kind === 'request' ? 'diagnostics' : 'operations';
    API.get(`/api/platform/${path}/${encodeURIComponent(selection.request_id)}`)
      .then((response) => {
        if (version !== detailVersion.current) return;
        if (!response.data.success) throw new Error(response.data.message);
        setStoredDetail({
          key: `${selection.kind}:${selection.request_id}`,
          data: response.data.data,
        });
        setEventId(selection.kind === 'request' ? 'receive' : 'input');
      })
      .catch((e) => {
        if (version === detailVersion.current)
          setDetailError(e.response?.data?.message || e.message);
      })
      .finally(() => {
        if (version === detailVersion.current) setDetailLoading(false);
      });
    return () => {
      detailVersion.current++;
    };
  }, [selection, refresh]);
  const detail =
    storedDetail?.key === `${selection?.kind}:${selection?.request_id}`
      ? storedDetail.data
      : null;
  useEffect(() => {
    if (selection?.kind !== 'request' && view === 'prompt') setView('records');
  }, [selection?.kind, view]);
  const events = projectEvents(selection?.kind, detail),
    event = events.find((e) => e.id === eventId);
  const diff =
    selection?.kind === 'operation' && detail
      ? changes(detail.before, detail.after)
      : [];
  const selectedDuration =
    selection?.kind === 'request'
      ? detail?.diagnostic?.total_ms
      : detail?.duration_ms;
  const exportRecord = () => {
    if (!detail) return;
    const blob = new Blob([JSON.stringify(detail, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selection.kind}-${selection.request_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <main className='trace-shell'>
      <header className='trace-heading'>
        <div>
          <span className='trace-eyebrow'>ACTIVITY WORKSPACE</span>
          <h1>操作日志</h1>
          <p>从请求与管理操作，追溯处理过程、结果与变更。</p>
        </div>
        <button
          type='button'
          onClick={() => setRefresh((v) => v + 1)}
          disabled={loading}
        >
          刷新记录
        </button>
      </header>
      <nav className='trace-tabs' aria-label='日志视图'>
        <button
          className={section === 'activity' ? 'active' : ''}
          onClick={() => setSection('activity')}
        >
          操作轨迹
        </button>
        <button
          className={section === 'ledger' ? 'active' : ''}
          onClick={() => setSection('ledger')}
        >
          历史流水
        </button>
      </nav>
      {section === 'ledger' ? (
        <section className='trace-legacy'>
          <LogsTable />
        </section>
      ) : (
        <>
          <form
            className='trace-filters'
            onSubmit={(e) => {
              e.preventDefault();
              setFilters({ ...draft });
              setPage(0);
            }}
          >
            <label>
              搜索
              <input
                placeholder='请求 ID、模型或操作名称'
                value={draft.q}
                onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              />
            </label>
            <label>
              记录类型
              <select
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              >
                <option value='all'>全部记录</option>
                <option value='request'>模型请求</option>
                <option value='operation'>管理操作</option>
              </select>
            </label>
            <label>
              结果
              <select
                value={draft.outcome}
                onChange={(e) =>
                  setDraft({ ...draft, outcome: e.target.value })
                }
              >
                <option value=''>全部结果</option>
                <option value='success'>成功</option>
                <option value='failed'>异常</option>
              </select>
            </label>
            <label>
              模型
              <input
                placeholder='精确模型名'
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
              />
            </label>
            <label>
              渠道
              <input
                type='number'
                min='1'
                placeholder='渠道 ID'
                value={draft.channel_id}
                onChange={(e) =>
                  setDraft({ ...draft, channel_id: e.target.value })
                }
              />
            </label>
            {isAdmin() && (
              <label>
                操作人／用户
                <input
                  type='number'
                  min='1'
                  placeholder='用户 ID'
                  value={draft.user_id}
                  onChange={(e) =>
                    setDraft({ ...draft, user_id: e.target.value })
                  }
                />
              </label>
            )}
            <label>
              开始时间
              <input
                type='datetime-local'
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
            </label>
            <label>
              结束时间
              <input
                type='datetime-local'
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </label>
            <div className='trace-filter-actions'>
              <button type='submit'>筛选</button>
              <button
                type='button'
                onClick={() => {
                  setDraft({ ...initialFilters, q: '' });
                  setFilters({ ...initialFilters, q: '' });
                  setPage(0);
                }}
              >
                重置
              </button>
            </div>
          </form>
          {error && (
            <div role='alert' className='trace-error'>
              {error}
              <button onClick={() => setRefresh((v) => v + 1)}>重试</button>
            </div>
          )}
          <div
            className={`trace-workspace trace-mobile-${mobilePane}`}
            ref={workspaceRef}
            style={{ '--trace-list-width': `${listWidth}px` }}
          >
            <aside className='trace-list'>
              <div className='trace-pane-header'>
                <strong>记录列表</strong>
                <span>{loading ? '加载中…' : `${total} 条匹配记录`}</span>
              </div>
              <div className='trace-list-items' aria-busy={loading}>
                {!rows.length && !loading && (
                  <div className='trace-empty'>
                    暂无匹配记录。新请求与管理操作会出现在这里；旧计费日志请查看历史流水。
                  </div>
                )}
                {rows.map((row) => (
                  <button
                    type='button'
                    key={`${row.kind}:${row.request_id}`}
                    aria-pressed={
                      row.kind === selection?.kind &&
                      row.request_id === selection?.request_id
                    }
                    className={`trace-run ${
                      row.kind === selection?.kind &&
                      row.request_id === selection?.request_id
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() => { setSelection(row); setMobilePane('detail'); }}
                  >
                    <div>
                      <span className='trace-kind'>
                        {row.kind === 'request'
                          ? row.path === '/v1/messages/count_tokens'
                            ? 'Token 计数'
                            : '模型请求'
                          : '管理操作'}
                      </span>
                      <span
                        className={`trace-badge ${
                          row.outcome === 'failed' ? 'failed' : 'success'
                        }`}
                      >
                        {row.outcome === 'failed' ? '异常' : '成功'}
                      </span>
                    </div>
                    <strong>{label(row)}</strong>
                    <small>
                      {time(row.created_at)} · {row.duration_ms} ms
                    </small>
                    <small>
                      {row.kind === 'operation' ? '操作人' : '用户'}{' '}
                      {row.user_id}
                      {row.subject_user_id
                        ? ` · 关联用户 ${row.subject_user_id}`
                        : ''}
                      {row.channel_id ? ` · 渠道 ${row.channel_id}` : ''}
                    </small>
                    {row.kind === 'request' && (
                      <div className='trace-run-metrics'>
                        <span
                          title={
                            row.input_tokens == null
                              ? '≈ 表示本地估算，不是供应商实际 usage'
                              : '供应商实际输入 Token'
                          }
                        >
                          <small>
                            {row.input_tokens == null &&
                            row.estimated_input_tokens > 0
                              ? 'Input 估算'
                              : 'Input Token'}
                          </small>
                          <strong>
                            {row.input_tokens == null
                              ? row.estimated_input_tokens > 0
                                ? `≈${row.estimated_input_tokens.toLocaleString()}`
                                : '—'
                              : row.input_tokens.toLocaleString()}
                          </strong>
                        </span>
                        <span title='供应商实际输出 Token，含计入输出的思考 Token'>
                          <small>Output Token</small>
                          <strong>
                            {row.output_tokens == null
                              ? row.path === '/v1/messages/count_tokens'
                                ? '不适用'
                                : '—'
                              : row.output_tokens.toLocaleString()}
                          </strong>
                        </span>
                        <span title='原始请求 messages/system/tools/input 的 JSON 字节数，不含输出参数'>
                          <small>Prompt Size</small>
                          <strong>
                            {row.prompt_bytes > 0
                              ? row.prompt_bytes < 1024
                                ? `${row.prompt_bytes} B`
                                : `${(row.prompt_bytes / 1024).toFixed(1)} KiB`
                              : '—'}
                          </strong>
                        </span>
                      </div>
                    )}
                    <code>{row.request_id}</code>
                  </button>
                ))}
              </div>
              <footer className='trace-pagination'>
                <button
                  disabled={page === 0 || loading}
                  onClick={() => setPage((v) => v - 1)}
                >
                  上一页
                </button>
                <span>
                  {page + 1} / {Math.max(1, Math.ceil(total / 25))}
                </span>
                <button
                  disabled={(page + 1) * 25 >= total || loading}
                  onClick={() => setPage((v) => v + 1)}
                >
                  下一页
                </button>
              </footer>
            </aside>
            <ListDivider
              width={listWidth}
              maximum={maximumWidth}
              onResize={resizeList}
              onCommit={() => persistWidth(listWidth)}
            />
            <section className='trace-main'>
              <div className='trace-pane-header'>
                <button className='trace-mobile-back' onClick={() => setMobilePane('list')}>← 返回列表</button>
                <strong>{selection ? label(selection) : '记录轨迹'}</strong>
                <button disabled={!detail} onClick={exportRecord}>
                  导出 JSON
                </button>
              </div>
              {selection && (
                <div className='trace-run-header'>
                  <code>{selection.request_id}</code>
                  <div className='trace-summary'>
                    <span>
                      结果
                      <strong>
                        {selection.outcome === 'failed' ? '异常' : '成功'}
                      </strong>
                    </span>
                    <span>
                      总耗时
                      <strong>
                        {selectedDuration ?? selection.duration_ms} ms
                      </strong>
                    </span>
                    <span>
                      {selection.kind === 'request' ? '模型请求' : '管理操作'}
                      <strong>
                        {selection.kind === 'request'
                          ? detail?.diagnostic?.actual_model || selection.model
                          : actions[detail?.action] || '—'}
                      </strong>
                    </span>
                  </div>
                </div>
              )}
              <nav className='trace-tabs trace-view-tabs'>
                {[
                  ['records', '事件记录'],
                  ['timeline', '时间线'],
                  ['raw', '原始数据'],
                  ['prompt', 'Prompt 内容'],
                ].map(([value, text]) => (
                  <button
                    key={value}
                    className={view === value ? 'active' : ''}
                    disabled={
                      value === 'prompt' && selection?.kind !== 'request'
                    }
                    onClick={() => setView(value)}
                  >
                    {text}
                  </button>
                ))}
              </nav>
              <div className='trace-content' aria-busy={detailLoading}>
                {detailLoading ? (
                  <div className='trace-empty'>加载轨迹详情…</div>
                ) : detailError ? (
                  <div role='alert' className='trace-error'>
                    {detailError}
                  </div>
                ) : !detail ? (
                  <div className='trace-empty'>选择一条记录查看处理过程。</div>
                ) : view === 'prompt' ? (
                  <PromptContent diagnostic={detail.diagnostic} />
                ) : view === 'raw' ? (
                  <pre className='trace-json'>{pretty(detail)}</pre>
                ) : view === 'timeline' ? (
                  <Timeline
                    events={events}
                    total={selectedDuration}
                    selected={eventId}
                    onSelect={setEventId}
                  />
                ) : (
                  <>
                    <div className='trace-events'>
                      {events.map((item, index) => (
                        <button
                          type='button'
                          key={item.id}
                          className={`trace-event ${
                            eventId === item.id ? 'selected' : ''
                          } ${item.failed ? 'failed' : ''}`}
                          onClick={() => setEventId(item.id)}
                        >
                          <span className='trace-step'>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <span className='trace-event-copy'>
                            <strong>{item.title}</strong>
                            <small>{item.note}</small>
                          </span>
                          <span className='trace-event-time'>
                            {item.at === null ? '快照' : `${item.at} ms`}
                          </span>
                        </button>
                      ))}
                    </div>
                    {diff.length > 0 && (
                      <section className='trace-diff'>
                        <h3>对象变更摘要</h3>
                        <p>操作前后观察快照的差异；可能包含并发变化。</p>
                        {diff.map((change) => (
                          <div key={change.key}>
                            <strong>
                              {fieldNames[change.key] || change.key}
                            </strong>
                            <del>{display(change.before)}</del>
                            <span>→</span>
                            <ins>{display(change.after)}</ins>
                          </div>
                        ))}
                      </section>
                    )}
                  </>
                )}
              </div>
            </section>
            <aside className='trace-inspector'>
              <div className='trace-pane-header'>
                <strong>事件详情</strong>
                <span>
                  {event?.at === null ? '快照' : event ? `${event.at} ms` : ''}
                </span>
              </div>
              {event ? (
                <div className='trace-inspector-content'>
                  <span className='trace-eyebrow'>SELECTED EVENT</span>
                  <h2>{event.title}</h2>
                  <p className='trace-muted'>{event.note}</p>
                  {event.fields && <Fields value={event.fields} />}
                  <h3>记录内容</h3>
                  {event.content ? (
                    <Fields value={event.content} />
                  ) : (
                    <p className='trace-muted'>
                      此事件没有保存正文或对象快照。
                    </p>
                  )}
                </div>
              ) : (
                <div className='trace-empty'>
                  点击事件或时间线片段查看详情。
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </main>
  );
}
